import { RouteLocation, RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES, SPLIT_NAMES } from '@/constants';
import { RouteTab } from '@/models/routing';
import { useUserStore } from '@/piniaStores/user/user.store';
import i18n, { loadTranslations } from '@/plugins/i18n';
import { canStaffAccessManageLicenses } from '@/utils/routeAuthz';

import { toNonVueRoute } from './utils';

const Applications = () => import('@/pages/Settings/Applications/Applications.vue');
const Brands = () => import('@/pages/Settings/Brands/Brands.vue');
const BrandAddEditForm = () => import('@/pages/Settings/Brands/components/BrandAddEditForm.vue');
const Billing = () => import('@/pages/Settings/Billing/Billing.vue');
const BillingDue = () => import('@/pages/Settings/BillingDue/BillingDue.vue');
const BuyerNotifications = () => import('@/pages/Settings/Notifications/BuyerNotifications/BuyerNotifications.vue');
const Integrations = () => import('@/pages/Settings/Integrations/Integrations.vue');
const IntegrationsSeller = () => import('@/pages/Settings/IntegrationsSeller/IntegrationsSeller.vue');
const IntegrationsSellerCustomerMapping = () =>
  import('@/pages/Settings/IntegrationsSeller/IntegrationsSellerCustomerMapping.vue');
const IntegrationsSellerProductMapping = () =>
  import('@/pages/Settings/IntegrationsSeller/IntegrationsSellerProductMapping.vue');
const GeneralSettings = () => import('@/pages/Settings/GeneralSettings/GeneralSettings.vue');
const Payments = () => import('@/pages/Settings/Payments/Payments.vue');
const AccountDetails = () => import('@/pages/Settings/Account/AccountDetails.vue');
const SettingsView = () => import('@/pages/Settings/index.vue');
const Users = () => import('@/pages/Settings/Users/Users.vue');
const FacilityOverview = () => import('@/pages/Settings/Facilities/FacilityOverview.vue');
const ManageLicenses = () => import('@/pages/Settings/Licenses/ManageLicenses.vue');
const ManageLicensesDue = () => import('@/pages/Settings/LicenseDue/LicenseDue.vue');
const PendingStoreRequests = () => import('@/pages/Settings/PendingStoreRequests/PendingStoreRequests.vue');
const Taxes = () => import('@/pages/Settings/Taxes/Taxes.vue');
const Onboarding = () => import('@/pages/Settings/Licenses/Onboarding.vue');

async function fetchTranslations() {
  await Promise.all([loadTranslations('enrollment'), loadTranslations('settings')]);
}

export function settingsTabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([
    {
      to: { name: ROUTE_NAMES.INFO },
      label: i18n.t('settings.tabs.companyInformation'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      // TODO - remove the logistics only check when completing the work for LOGX-111
      visible: ({ company, userPermissions, isSplitEnabled }) =>
        !isSplitEnabled(SPLIT_NAMES.SELLER_SETTINGS_VUE) &&
        company?.seller &&
        !company?.isLogisticsOnly &&
        userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.SELLER_SETTINGS_NEW },
      label: i18n.t('settings.tabs.companyInformation'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      // TODO - remove the logistics only check when completing the work for LOGX-111
      visible: ({ company, userPermissions, isSplitEnabled }) =>
        isSplitEnabled(SPLIT_NAMES.SELLER_SETTINGS_VUE) &&
        company?.seller &&
        !company?.isLogisticsOnly &&
        userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.SELLER_GENERAL_SETTINGS },
      label: i18n.t('settings.generalSettings'),
      visible: ({ company, isSplitEnabled }) => {
        return company?.seller && isSplitEnabled(SPLIT_NAMES.SELLER_SETTINGS_VUE);
      },
    },
    {
      to: { name: ROUTE_NAMES.BUYER_SETTINGS_ACCOUNT_DETAILS },
      label: i18n.t('settings.tabs.accountDetails'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) => company?.buyer && !company?.seller && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.BRAND_OWNER_SETTINGS },
      label: i18n.t('settings.tabs.brandOwnerAccountSettings'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) =>
        company?.isBrandOwner &&
        !company?.buyer &&
        !company?.seller &&
        !company?.isLogisticsOnly &&
        userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.BRAND_OWNER_BRANDS },
      label: i18n.t('settings.tabs.brandOwnerBrands'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) =>
        company?.isBrandOwner && !company?.isLogisticsOnly && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.DELIVERY_PROVIDER_SETTINGS },
      label: i18n.t('settings.tabs.companyInformation'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) =>
        company?.isDeliveryProvider && !company?.buyer && !company?.seller && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.USERS },
      label: i18n.t('settings.tabs.users'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      // TODO: remove the logistics only check when completing the work for LOGX-112
      visible: ({ company, userPermissions }) => !company?.isLogisticsOnly && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.BUYER_NOTIFICATIONS },
      label: i18n.t('settings.tabs.notifications'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) => company?.buyer && !company?.seller && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.ORDERS },
      label: i18n.t('settings.tabs.orders'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) =>
        company?.seller && !company?.isLogisticsOnly && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.BRANDS },
      label: i18n.t('settings.tabs.brands'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) =>
        company?.seller && !company?.isLogisticsOnly && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.TAXES },
      label: i18n.t('settings.tabs.taxes'),
      visible: ({ company, userPermissions, isSplitEnabled }) =>
        company?.seller && userPermissions?.staffIsAdmin && isSplitEnabled(SPLIT_NAMES.SETTINGS_TAX_SCHEDULES),
    },
    {
      to: { name: ROUTE_NAMES.PAYMENTS_SETTINGS },
      label: i18n.t('settings.tabs.payments'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) => company?.seller && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.INVOICE },
      label: i18n.t('settings.tabs.invoices'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) =>
        company?.seller && !company?.isLogisticsOnly && userPermissions?.staffIsAdmin,
    },
    {
      to: useUserStore().getIsSplitEnabled('traceability_pod__biotrack_new_york')
        ? { name: ROUTE_NAMES.COMPLIANCE }
        : { name: ROUTE_NAMES.LICENSES },
      label: () => {
        return useUserStore().getIsSplitEnabled('traceability_pod__biotrack_new_york')
          ? i18n.t('settings.tabs.compliance')
          : i18n.t('settings.tabs.manageLicenses');
      },
      disabled: ({ isDelinquent }) => isDelinquent,
      // TODO: remove the logistics only check when completing the work for LOGX-114
      visible: ({ company, userPermissions }) => {
        return (
          !company?.isDeliveryProvider &&
          !company?.isBrandOwner &&
          !company?.isLogisticsOnly &&
          (userPermissions?.staffIsAdmin || canStaffAccessManageLicenses())
        );
      },
    },
    {
      to: { name: ROUTE_NAMES.BILLING },
      label: () => {
        const { getIsSplitEnabled } = useUserStore();
        const isPlansAndBillingEnabled = getIsSplitEnabled(SPLIT_NAMES.PLANS_AND_BILLING);
        return isPlansAndBillingEnabled ? i18n.t('settings.tabs.plansAndBilling') : i18n.t('settings.tabs.billing');
      },
      disabled: ({ isLicenseLocked }) => isLicenseLocked,
      // TODO: remove the logistics only check when completing the work for LOGX-2
      visible: ({ company, userPermissions }) =>
        !company?.isLogisticsOnly && userPermissions?.staffIsAdmin && userPermissions?.canManageBilling,
    },
    {
      to: { name: ROUTE_NAMES.INTEGRATIONS_SELLER },
      label: i18n.t('settings.tabs.integrations'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, isSplitEnabled, userPermissions }) =>
        isSplitEnabled(SPLIT_NAMES.INTEGRATIONS_SELLER) && company?.seller && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.EMAILS },
      label: i18n.t('settings.tabs.notifications'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) =>
        company?.seller && !company?.isLogisticsOnly && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.REPORTS_SETTINGS },
      label: i18n.t('settings.tabs.reports'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) =>
        company?.seller && !company?.isLogisticsOnly && userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.APPLICATIONS },
      label: i18n.t('settings.tabs.applications'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, featureFlags, userPermissions }) => {
        return (
          userPermissions?.staffIsAdmin &&
          !!(
            (company?.buyer && featureFlags?.enableBuyerApplications) ||
            (company?.seller && !company?.isLogisticsOnly && featureFlags?.enableSellerApplications) ||
            (company?.isBrandOwner && featureFlags?.enableBMEApplications)
          )
        );
      },
    },
    {
      to: { name: ROUTE_NAMES.DEVELOPER_SETTINGS },
      label: i18n.t('settings.tabs.developerOptions'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) =>
        !company?.isLogisticsOnly && (company?.webhooksEnabled || company?.apiEnabled) && userPermissions?.canAccessApi,
    },
    {
      to: { name: ROUTE_NAMES.INTEGRATIONS },
      label: i18n.t('settings.tabs.integrations'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, featureFlags }) => company?.buyer && featureFlags.enableBuyerIntegrations,
    },
    {
      to: { name: ROUTE_NAMES.BUYER_REGISTER_STORE },
      label: i18n.t('settings.tabs.register'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions, isSplitEnabled }) =>
        !isSplitEnabled(SPLIT_NAMES.BUYER_SETTINGS_STORE_REQUESTS_VUE) &&
        company?.buyer &&
        userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.PENDING_STORE_REQUESTS },
      label: i18n.t('settings.tabs.pendingStoreRequests'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions, isSplitEnabled }) =>
        isSplitEnabled(SPLIT_NAMES.BUYER_SETTINGS_STORE_REQUESTS_VUE) &&
        company?.buyer &&
        userPermissions?.staffIsAdmin,
    },
    {
      to: { name: ROUTE_NAMES.ACCOUNT_OWNER_SETTINGS },
      label: i18n.t('settings.tabs.accountOwnerSettings'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions }) => !company?.isLogisticsOnly && userPermissions?.staffIsAccountOwner,
    },
    {
      to: { name: ROUTE_NAMES.FACILITY_MANAGEMENT },
      label: i18n.t('settings.tabs.facilityManagement'),
      disabled: ({ isDelinquent, isLicenseLocked }) => isDelinquent || isLicenseLocked,
      visible: ({ company, userPermissions, isSplitEnabled }) =>
        company?.seller &&
        !company?.isLogisticsOnly &&
        userPermissions?.staffIsAdmin &&
        isSplitEnabled(SPLIT_NAMES.INVENTORY_FACILITY),
    },
  ]);
}

const settingsRoutes: RouteRecordRaw[] = [
  // Vue routes
  {
    component: SettingsView,
    path: 'settings/',
    beforeEnter: [fetchTranslations],

    children: [
      {
        path: 'seller-new',
        name: ROUTE_NAMES.SELLER_SETTINGS_NEW,
        component: AccountDetails,
        props: {
          isSeller: true,
        },
        meta: {
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.account.title'),
          description: () => i18n.t('settings.account.description'),
          permissions: [
            { rules: ['seller_settings_vue'], redirect: { name: ROUTE_NAMES.INFO } },
            { rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] },
          ],
        },
      },
      {
        path: 'buyer/account-details',
        name: ROUTE_NAMES.BUYER_SETTINGS_ACCOUNT_DETAILS,
        component: AccountDetails,
        props: {
          isSeller: false,
        },
        meta: {
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.account.title'),
          description: () => i18n.t('settings.account.description'),
          hasServerRenderedContent() {
            return !useUserStore().getIsSplitEnabled('settings__buyer_account_details');
          },
          permissions: [
            {
              rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)],
            },
          ],
        },
      },
      {
        path: 'buyer/notifications',
        name: ROUTE_NAMES.BUYER_NOTIFICATIONS,
        component: BuyerNotifications,
        meta: {
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.notifications.title'),
          description: () => i18n.t('settings.notifications.description'),
          hasServerRenderedContent() {
            return !useUserStore().getIsSplitEnabled(SPLIT_NAMES.VUE_BUYER_NOTIFICATION_SETTINGS);
          },
          permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] }],
        },
      },
      {
        path: 'applications',
        name: ROUTE_NAMES.APPLICATIONS,
        component: Applications,
        meta: {
          tabs: settingsTabRoutes,
          title: i18n.t('settings.applications.title'),
          permissions: [
            {
              rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)],
            },
            {
              rules: [
                ({ userActiveCompany, user, activeCompany }) =>
                  Boolean(userActiveCompany?.buyer && user.featureFlags.enableBuyerApplications) ||
                  Boolean(userActiveCompany?.seller && user.featureFlags.enableSellerApplications) ||
                  Boolean(activeCompany?.isBrandOwner && user.featureFlags.enableBMEApplications),
              ],
            },
          ],
        },
      },
      {
        path: 'licenses/',
        name: ROUTE_NAMES.LICENSES,
        component: ManageLicenses,
        beforeEnter(to, _from, next) {
          const { getIsSplitEnabled } = useUserStore();
          if (getIsSplitEnabled('traceability_pod__biotrack_new_york')) {
            return next({ name: ROUTE_NAMES.COMPLIANCE, params: to.params });
          }
          next();
        },
        meta: {
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.tabs.manageLicenses'),
          permissions: [
            {
              rules: [
                ({ activeCompany, userActiveCompany }) => {
                  return (
                    !!(
                      userActiveCompany?.staffIsAdmin &&
                      !activeCompany?.isDeliveryProvider &&
                      !activeCompany?.isBrandOwner &&
                      !activeCompany?.isLogisticsOnly
                    ) || canStaffAccessManageLicenses()
                  );
                },
              ],
            },
          ],
        },
      },
      {
        path: 'compliance/',
        name: ROUTE_NAMES.COMPLIANCE,
        component: ManageLicenses,
        meta: {
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.tabs.compliance'),
          permissions: [
            {
              rules: [
                ({ activeCompany, userActiveCompany }) => {
                  return (
                    !!(
                      userActiveCompany?.staffIsAdmin &&
                      !activeCompany?.isDeliveryProvider &&
                      !activeCompany?.isBrandOwner &&
                      !activeCompany?.isLogisticsOnly
                    ) || canStaffAccessManageLicenses()
                  );
                },
              ],
            },
          ],
        },
      },
      {
        path: 'licenses/onboarding/',
        name: ROUTE_NAMES.LICENSES_INTEGRATION_ONBOARDING,
        component: Onboarding,
        beforeEnter(to, _from, next) {
          const { getIsSplitEnabled } = useUserStore();
          if (getIsSplitEnabled('traceability_pod__biotrack_new_york')) {
            return next({ name: ROUTE_NAMES.COMPLIANCE_INTEGRATION_ONBOARDING, params: to.params });
          }
          next();
        },
        meta: {
          title: () => i18n.t('settings.seedToSaleIntegration.setUpIntegrationMetrc'),
          permissions: [
            {
              rules: ['canAccessSeedToSaleIntegration', ({ userActiveCompany }) => !!userActiveCompany?.staffIsAdmin],
            },
            {
              rules: [
                ({ isSplitEnabled, userActiveCompany }) =>
                  (isSplitEnabled('manage_licenses__new_metrc_flow') && !!userActiveCompany?.seller) ||
                  (isSplitEnabled('ce_pod__buyer_metrc_onboarding') && !!userActiveCompany?.buyer),
              ],
            },
          ],
        },
      },
      {
        path: 'compliance/onboarding/',
        name: ROUTE_NAMES.COMPLIANCE_INTEGRATION_ONBOARDING,
        component: Onboarding,
        meta: {
          title: () => i18n.t('settings.seedToSaleIntegration.setUpIntegrationMetrc'),
          permissions: [
            {
              rules: ['canAccessSeedToSaleIntegration', ({ userActiveCompany }) => !!userActiveCompany?.staffIsAdmin],
            },
            {
              rules: [
                ({ isSplitEnabled, userActiveCompany }) =>
                  (isSplitEnabled('manage_licenses__new_metrc_flow') && !!userActiveCompany?.seller) ||
                  (isSplitEnabled('ce_pod__buyer_metrc_onboarding') && !!userActiveCompany?.buyer),
              ],
            },
          ],
        },
      },
      {
        path: 'licenses-due',
        name: ROUTE_NAMES.LICENSES_DUE,
        component: ManageLicensesDue,
        meta: {
          title: () => i18n.t('settings.tabs.manageLicenses'),
        },
      },
      {
        path: 'pending-store-requests',
        name: ROUTE_NAMES.PENDING_STORE_REQUESTS,
        component: PendingStoreRequests,
        meta: {
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.tabs.pendingStoreRequests'),
          permissions: [
            { rules: ['buyer_settings_store_requests_vue'], redirect: { name: ROUTE_NAMES.BUYER_REGISTER_STORE } },
            { rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] },
          ],
        },
      },
      {
        path: 'billing',
        name: ROUTE_NAMES.BILLING,
        component: Billing,
        meta: {
          tabs: settingsTabRoutes,
          title: i18n.t('settings.billing.title'),
          permissions: [
            { rules: ['canManageBilling', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)] },
          ],
        },
      },
      {
        path: 'billing-due',
        name: ROUTE_NAMES.BILLING_DUE,
        component: BillingDue,
        meta: {
          title: i18n.t('settings.billing.title'),
          permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.seller)] }],
        },
      },
      {
        path: 'brands',
        name: ROUTE_NAMES.BRANDS,
        component: Brands,
        meta: {
          includeNav: true,
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.brands.title'),
          hasServerRenderedContent() {
            return !useUserStore().getIsSplitEnabled(SPLIT_NAMES.VUE_SETTINGS_BRANDS_PAGE);
          },
          permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)] }],
        },
      },
      {
        path: 'brands/edit/:brandId',
        name: ROUTE_NAMES.BRAND_EDIT,
        component: BrandAddEditForm,
        props: {
          isEdit: true,
        },
        meta: {
          title: () => i18n.t('settings.brands.title'),
          hasServerRenderedContent() {
            return !useUserStore().getIsSplitEnabled(SPLIT_NAMES.VUE_SETTINGS_BRANDS_PAGE);
          },
          permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] }],
        },
      },
      {
        path: 'brands/add',
        name: ROUTE_NAMES.BRAND_ADD,
        component: BrandAddEditForm,
        props: {
          isEdit: false,
        },
        meta: {
          title: () => i18n.t('settings.brands.title'),
          hasServerRenderedContent() {
            return !useUserStore().getIsSplitEnabled(SPLIT_NAMES.VUE_SETTINGS_BRANDS_PAGE);
          },
          permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] }],
        },
      },
      {
        path: 'integrations',
        name: ROUTE_NAMES.INTEGRATIONS,
        component: Integrations,
        meta: {
          tabs: settingsTabRoutes,
          title: i18n.t('settings.integrations.title'),
          description: i18n.t('settings.integrations.description'),
          permissions: [
            {
              rules: [
                ({ userActiveCompany, user }) =>
                  Boolean(userActiveCompany?.buyer && user.featureFlags.enableBuyerApplications),
              ],
            },
          ],
        },
      },
      {
        path: 'seller/integrations',
        name: ROUTE_NAMES.INTEGRATIONS_SELLER,
        component: IntegrationsSeller,
        meta: {
          tabs: settingsTabRoutes,
          title: i18n.t('settings.integrations.title'),
          description: i18n.t('settings.integrations.description'),
          permissions: [
            {
              rules: [
                'npr_pod__integrations_seller',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin && userActiveCompany.seller),
              ],
            },
          ],
        },
      },
      {
        path: 'seller/integrations/:connectionId/customer-mapping',
        name: ROUTE_NAMES.INTEGRATIONS_SELLER_CUSTOMER_MAPPING,
        component: IntegrationsSellerCustomerMapping,
        props: true,
        meta: {
          title: i18n.t('settings.integrations.title'),
          description: i18n.t('settings.integrations.description'),
          permissions: [
            {
              rules: [
                'npr_pod__integrations_seller',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin && userActiveCompany.seller),
              ],
            },
          ],
        },
      },
      {
        path: 'seller/integrations/:connectionId/product-mapping',
        name: ROUTE_NAMES.INTEGRATIONS_SELLER_PRODUCT_MAPPING,
        component: IntegrationsSellerProductMapping,
        props: true,
        meta: {
          title: i18n.t('settings.integrations.title'),
          description: i18n.t('settings.integrations.description'),
          permissions: [
            {
              rules: [
                'npr_pod__integrations_seller',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin && userActiveCompany.seller),
              ],
            },
          ],
        },
      },
      {
        path: 'users',
        name: ROUTE_NAMES.USERS,
        component: Users,
        meta: {
          description: () => i18n.t('settings.users.description'),
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.users.title'),
          includeNav: true,
          permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] }],
        },
      },
      {
        // /settings/add-user-new -> /settings/add-user
        path: 'add-user-new',
        redirect: (to) => ({ name: ROUTE_NAMES.ADD_USER, params: { company: to.params.company } }),
      },
      {
        path: 'add-user',
        name: ROUTE_NAMES.ADD_USER,
        component: Users,
        meta: {
          includeNav: true,
          description: () => i18n.t('settings.users.description'),
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.users.title'),
          permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] }],
        },
      },
      {
        path: 'users/edit/:id/',
        name: ROUTE_NAMES.EDIT_USERS,
        component: Users,
        meta: {
          includeNav: true,
          description: () => i18n.t('settings.users.description'),
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.users.title'),
          permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] }],
        },
      },
      {
        path: 'seller/general/',
        name: ROUTE_NAMES.SELLER_GENERAL_SETTINGS,
        component: GeneralSettings,
        beforeEnter: [fetchTranslations],
        meta: {
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.generalSettings'),
          permissions: [
            { rules: ['seller_settings_vue', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] },
          ],
        },
      },
      {
        path: 'seller/payments',
        name: ROUTE_NAMES.PAYMENTS_SETTINGS,
        component: Payments,
        beforeEnter: [fetchTranslations],
        meta: {
          tabs: settingsTabRoutes,
          title: i18n.t('settings.tabs.payments'),
          permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] }],
        },
      },
      {
        path: 'seller/facilities',
        name: ROUTE_NAMES.FACILITY_MANAGEMENT,
        component: FacilityOverview,
        meta: {
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.tabs.facilityManagement'),
          description: () => i18n.t('settings.facilityManagement.description'),
          permissions: [
            {
              rules: [
                'canManageInventory',
                'inventory_pod__inventory_warehouse_support',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin && userActiveCompany.seller),
              ],
            },
          ],
        },
      },
      {
        path: 'seller/taxes',
        name: ROUTE_NAMES.TAXES,
        component: Taxes,
        meta: {
          tabs: settingsTabRoutes,
          title: () => i18n.t('settings.tabs.taxes'),
          permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] }],
        },
      },
    ],
  },
  // TODO: Remove this redirect after the old settings page is deprecated
  // Doing this redirect here instead of Django router to avoid changes on the API endpoints
  // So that, the app will redirect only if the browser requires the page as a GET (full page reload or client-side navigation)
  {
    // /brands -> /settings/brands
    path: 'brands/',
    redirect: (to) => ({ name: ROUTE_NAMES.BRANDS, params: { company: to.params.company } }),
  },

  // Legacy routes
  toNonVueRoute({ path: 'register-store/', name: ROUTE_NAMES.BUYER_REGISTER_STORE }),
  toNonVueRoute({ path: 'settings/account-owner/', name: ROUTE_NAMES.ACCOUNT_OWNER_SETTINGS }),
  toNonVueRoute({ path: 'settings/brand-owner/', name: ROUTE_NAMES.BRAND_OWNER_SETTINGS }),
  toNonVueRoute({ path: 'settings/brand-owner/brands/', name: ROUTE_NAMES.BRAND_OWNER_BRANDS }),
  toNonVueRoute({ path: 'settings/buyer/notification', name: ROUTE_NAMES.BUYER_NOTIFICATIONS_LEGACY }),
  toNonVueRoute({ path: 'settings/delivery-provider/', name: ROUTE_NAMES.DELIVERY_PROVIDER_SETTINGS }),
  toNonVueRoute({ path: 'settings/developers/', name: ROUTE_NAMES.DEVELOPER_SETTINGS }),
  toNonVueRoute({ path: 'settings/seller/emails/', name: ROUTE_NAMES.EMAILS }),
  toNonVueRoute({ path: 'settings/seller/info/', name: ROUTE_NAMES.INFO }),
  toNonVueRoute({ path: 'settings/seller/invoice/', name: ROUTE_NAMES.INVOICE }),
  toNonVueRoute({ path: 'settings/seller/orders/', name: ROUTE_NAMES.ORDERS }),
  toNonVueRoute({ path: 'settings/seller/reports/', name: ROUTE_NAMES.REPORTS_SETTINGS }),
  toNonVueRoute({
    path: 'settings/invite-user/',
    name: ROUTE_NAMES.INVITE_USER,
    beforeEnter(to: RouteLocation, _from, next) {
      // Redirects user: /settings/invite-user -> /settings/add-user
      return next({ name: ROUTE_NAMES.ADD_USER, params: { company: to.params.company } });
    },
    meta: {
      permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffIsAdmin)] }],
    },
  }),
];

export default settingsRoutes;
